/* eslint-disable prettier/prettier */
import type { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchForAllCountriesStaticProps } from 'src/actions/searchActions';
import { H3 } from 'src/components/h3/h3';
import { NavFooter } from 'src/components/nav/navFooter.component';
import { ParagraphHeader } from 'src/components/paragraph-header/paragraph-header';
import { SecondaryParagraph } from 'src/components/secondary-paragraph/secondary-paragraph';
import { userActions } from 'src/reducers/userReducers';
import type { RootState } from 'src/store';
import { scrollTo } from 'src/utils/scrollTo';
import StandardSEOWrapper from '../components/layout/standardSEOWrapper.component';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function Privacy({ countries }: any) {
  const dispatch = useDispatch();

  const { isCommingFromLearnMore } = useSelector(
    (state: RootState) => state.user
  );

  useEffect(() => {
    if (isCommingFromLearnMore) {
      scrollTo('cookies');
    }
    if (isCommingFromLearnMore) {
      setTimeout(() => {
        dispatch(
          userActions.setIsCommingFromLearnMore({
            isCommingFromLearnMore: false,
          })
        );
      }, 100);
    }
  }, []);

  type itemsTypes =
    | 'summary'
    | 'privacy-policy'
    | 'introduction'
    | 'collection'
    | 'sharing'
    | 'protection'
    | 'important';

  type collectionTypes = 'types' | 'information' | 'how' | 'other';

  type protectionTypes = 'how-do' | 'how-long' | 'your-rights';

  type importantTypes = 'where' | 'our' | 'changes' | 'contact' | 'thank-you';

  const [activeItem, setActiveItem] = useState<itemsTypes>('summary');
  const [collectionItem, setCollectionItem] =
    useState<collectionTypes>('types');
  const [protectionItem, setProtectionItem] =
    useState<protectionTypes>('how-do');
  const [importantItem, setImportantItem] = useState<importantTypes>('where');

  const handleActiveItem = (item: itemsTypes) => () => {
    setActiveItem(item);
  };

  const handleCollectionItem = (item: collectionTypes) => () => {
    setCollectionItem(item);
    scrollTo(item);
  };

  const handleProtectionItem = (item: protectionTypes) => () => {
    setProtectionItem(item);
    scrollTo(item);
  };

  const handleImportantItem = (item: importantTypes) => () => {
    setImportantItem(item);
    scrollTo(item);
  };

  type MobileItemsTypes =
    | 'summary'
    | 'introduction'
    | 'types'
    | 'information'
    | 'how-we'
    | 'when-may'
    | 'how-do'
    | 'how-long'
    | 'your-rights'
    | 'where'
    | 'our'
    | 'changes'
    | 'contact'
    | 'thank-you';

  const handleMobileActiveItem = (item: MobileItemsTypes) => () => {
    scrollTo(item);
  };

  const TableSection = ({
    leftText,
    rightText,
  }: {
    leftText: string;
    rightText: string;
  }) => {
    return (
      <div className="flex">
        <div className="flex w-[207px] items-center border-r-[1.5px] border-t-[1.5px] border-r-[#E8E8E8] border-t-[#E8E8E8] bg-[#409F8505]">
          <p className="p-[10px] text-[14px] font-[400] leading-[137%]">
            {leftText}
          </p>
        </div>
        <div className="flex w-[498px] items-center border-t-[1.5px] border-t-[#E8E8E8] bg-[#409F8505]">
          <p className="p-[10px] text-[14px] font-[400] leading-[137%]">
            {rightText}
          </p>
        </div>
      </div>
    );
  };

  const MobileTableSection = ({
    leftText,
    rightText,
  }: {
    leftText: string;
    rightText: string;
  }) => {
    return (
      <div className="flex">
        <div className="flex w-[124px] items-center border-r-[1.5px] border-t-[1.5px] border-r-[#E8E8E8] border-t-[#E8E8E8] bg-[#409F8505]">
          <p className="p-[10px] text-[12px] font-[400] leading-[137%]">
            {leftText}
          </p>
        </div>
        <div className="flex w-[250px] items-center border-t-[1.5px] border-t-[#E8E8E8] bg-[#409F8505]">
          <p className="p-[10px] text-[12px] font-[400] leading-[137%]">
            {rightText}
          </p>
        </div>
      </div>
    );
  };

  const { t } = useTranslation(['privacy']);

  return (
    <div className="flex min-h-screen flex-col">
      <StandardSEOWrapper
        title={t('privacy:seo.title')}
        description={t('privacy:seo.description')}
      />
      <div className="flex flex-col pt-[10px] xl:hidden">
        <ParagraphHeader className="px-[16px] opacity-70" fontSize={18}>
          Table of Contents
        </ParagraphHeader>
        <div className="mt-[14px] flex flex-col px-[16px]">
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('summary')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              Summary
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('introduction')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              Introduction
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('types')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              Types of Information We Receive
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('information')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              Information Related to Your Use of the Site
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('how-we')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              How We Use Your Information
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('when-may')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              When May We Share Your Information?
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('how-do')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              How Do We Protect Your Personal Information?
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('how-long')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              How Long Do We Keep Your Data?
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('your-rights')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              Your Rights
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('where')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              Where is the Population Project?
            </SecondaryParagraph>
          </button>
          <button className="mb-[16px]" onClick={handleMobileActiveItem('our')}>
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              Our Response to DNT Signals
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('changes')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              Changes to This Privacy Policy
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('contact')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              Contact Us
            </SecondaryParagraph>
          </button>
          <button
            className="mb-[16px]"
            onClick={handleMobileActiveItem('thank-you')}
          >
            <SecondaryParagraph
              className={`text-left underline transition`}
              fontWheight={400}
              fontSize={14}
            >
              Thank You!
            </SecondaryParagraph>
          </button>
        </div>
        <H3 fontSize={20} className="summary mt-[72px] px-[16px]">
          Summary of the Privacy Policy
        </H3>
        <div className="mx-[16px] mb-[30px] mt-[6px] h-px bg-[#D1D1D1]" />
        <p
          className={`px-[16px] pb-[26px] text-[16px] font-[400] leading-[137%]`}
        >
          This is a summary of the Privacy Policy, To read the full terms,
          choose a page in the table of contents to the left.
        </p>

        <p
          className={`px-[16px] pb-[48px] text-[14px] font-[400] italic leading-[137%]`}
        >
          Disclaimer: This summary is not a part of the Privacy Policy and is
          not a legal document. It is simply a handy reference for understanding
          the full Privacy Policy. Think of it as the user-friendly interface to
          our Privacy Policy.
        </p>

        <div className={`mb-[30px] w-full bg-[#409F8510] px-[16px] py-[15px]`}>
          <p className={`text-[18px] font-[600] leading-[137%] `}>
            Because we believe that you shouldn’t have to provide personal
            information to participate to the Population Project, you may:
          </p>
          <div className="ml-[30px] flex w-auto flex-col">
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[18px] font-[400] leading-[137%]">
                Read, edit, or use the Population Project Site without
                registering for an account.
              </p>
            </div>
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[18px] font-[400] leading-[137%]">
                Register for an account without providing an email address or
                real name.
              </p>
            </div>
          </div>

          <p className={`pt-[30px] text-[16px] font-[600] leading-[137%] `}>
            Because we want to understand how the Population Project Site is
            used so we can make it better for you, we collect some information
            from you:
          </p>
          <div className="ml-[30px] flex w-auto flex-col">
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                Make public contributions.
              </p>
            </div>
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                Register an account or update your user page.
              </p>
            </div>
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                Use the Population Project Site.
              </p>
            </div>
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                Send us emails or participate in a survey or give feedback.
              </p>
            </div>
          </div>

          <p className={`pt-[30px] text-[16px] font-[600] leading-[137%] `}>
            We are committed to:
          </p>
          <div className="ml-[30px] flex w-auto flex-col">
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                Describing how your information may be used or shared in this
                Privacy Policy.
              </p>
            </div>
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                Using reasonable measures to keep your information secure.
              </p>
            </div>
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                Never selling your information or sharing it with third parties
                for marketing purposes.
              </p>
            </div>
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                Only sharing your information in limited circumstances, such as
                to improve the Population Project Site or to comply with the
                law.
              </p>
            </div>
          </div>

          <p className={`pt-[30px] text-[16px] font-[600] leading-[137%] `}>
            Be aware:
          </p>
          <div className="ml-[30px] flex w-auto flex-col">
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                Any content you add or any changes that you make to the
                Population Project Site will be publicly and permanently
                available.
              </p>
            </div>
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                If you add content or make a change to the Population Project
                Site without logging in, that content or change will be publicly
                and permanently attributed to the IP address used at the time
                rather than a username.
              </p>
            </div>
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                As part of our commitment to education and research around the
                world, we may occasionally release public information and
                aggregated or non-personal information to the general public
                through data dumps and data sets.
              </p>
            </div>
            <div className="flex pt-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p className="text-[16px] font-[400] leading-[137%]">
                For the protection of the Population Project and other users, if
                you do not agree with this Privacy Policy, you may not use the
                Population Project’s Site.
              </p>
            </div>
          </div>
        </div>

        <H3 fontSize={20} className="introduction mt-[72px] px-[16px]">
          Introduction to the Privacy Policy
        </H3>
        <div className="mx-[16px] mb-[30px] mt-[6px] h-px bg-[#D1D1D1]" />
        <p
          className={`px-[16px] pb-[26px] text-[16px] font-[400] leading-[137%]`}
        >
          The Population Project relies heavily on the work and contributions of
          volunteers. We believe that information-gathering and use should go
          hand-in-hand with transparency. This Privacy Policy explains how the
          Population Project, the non-profit organization that hosts this site,
          collects, uses, and shares information we receive from you through
          your use of the Population Project Site. It is essential to understand
          that, by using the Population Project Site, you consent to the
          collection, transfer, processing, storage, disclosure, and use of your
          information as described in this Privacy Policy. That means that
          reading this Policy carefully is important.
          <br />
          <br />
          We believe that you shouldn't have to provide nonpublic Personal
          Information to participate to the Population Project. You do not have
          to provide things like your real name, address, or country to sign up
          for a standard account or contribute content to the Population Project
          Site.
          <br />
          <br />
          We do not sell or rent your Personal Information, nor do we give it to
          others to sell you anything. We use it to figure out how to make the
          Population Project Site more engaging and accessible. Put simply: we
          use this information to make the Population Project Site better for
          you.
          <br />
          <br />
          Because everyone (not just lawyers) should be able to easily
          understand how and why their information is collected and used, we use
          common language instead of more formal terms throughout this Policy.
          To help ensure your understanding of some particular key terms, here
          is a table of translations:
        </p>

        <div className={`mb-[30px] flex w-full flex-col`}>
          <div className="flex">
            <div className="flex w-[124px] items-center justify-center border-r-[1.5px] border-r-[#E8E8E8] bg-[#409F8515]">
              <p className="py-[14px] text-center text-[12px] font-[600] leading-[137%]">
                When we say...
              </p>
            </div>
            <div className="flex w-[250px] items-center justify-center bg-[#409F8515]">
              <p className="py-[14px] text-center text-[12px] font-[600] leading-[137%]">
                ...we mean:
              </p>
            </div>
          </div>
          <MobileTableSection
            leftText="“the Population Project” / ”we” / ”us” / ”our”"
            rightText="The Population Project, the non-profit organization that
                  operates this Site."
          />
          <MobileTableSection
            leftText="“the Population Project Site” / ”the Site” / ”our services”"
            rightText="The site whose URL is thepopulationproject.org, as well as mobile applications, Application Programming Interfaces (APIs), emails, and notifications."
          />
          <MobileTableSection
            leftText="“you” / “your” / “me”"
            rightText="You, regardless of whether you are an individual, group, or organization, and regardless of whether you are using the Site on behalf of yourself or someone else."
          />
          <MobileTableSection
            leftText="“this Policy” / “this Privacy Policy”"
            rightText="This document, entitled the “Population Project Privacy Policy”."
          />
          <MobileTableSection
            leftText="“contributions”"
            rightText="Content you add or changes you make to the Site."
          />
          <div className="flex">
            <div className="flex w-[124px] items-center justify-center border-r-[1.5px] border-t-[1.5px] border-r-[#E8E8E8] border-t-[#E8E8E8] bg-[#409F8505]">
              <p className="p-[10px] text-[12px] font-[400] leading-[137%]">
                “Personal Information”
              </p>
            </div>
            <div className="flex w-[250px] flex-col items-center justify-center border-t-[1.5px] border-t-[#E8E8E8] bg-[#409F8505]">
              <p className="p-[10px] text-[12px] font-[400] leading-[137%]">
                Information you provide us or information we collect that could
                be used to personally identify you. To be clear, while we do not
                necessarily collect the following types of information, we
                consider at least the following to be “Personal Information” if
                it is otherwise nonpublic and can be used to identify you:
              </p>
              <p className="ml-[28px] p-[10px] text-[12px] font-[400] leading-[137%]">
                (a) your real name, address, phone number, email address,
                password, identification number on government-issued ID, IP
                address, user-agent information, payment and account number (b)
                when associated with one of the items in subsection (a), any
                sensitive data such as date of birth, gender, sexual
                orientation, racial or ethnic origins, marital or familial
                status, medical conditions or disabilities, political
                affiliation, and religion.
              </p>
            </div>
          </div>
          <MobileTableSection
            leftText="“third party” / “third parties”"
            rightText="Individuals, entities, websites, services, products, and applications that are not controlled, managed, or operated by the Population Project."
          />
        </div>

        <H3 fontSize={20} className="mt-[72px] px-[16px]">
          Collection & Use of Info
        </H3>
        <div className="mx-[16px] mb-[30px] mt-[6px] h-px bg-[#D1D1D1]" />

        <ParagraphHeader className="types mb-[25px] px-[16px]" fontWeight={700}>
          Types of Information We Receive From You & How We Get It
        </ParagraphHeader>
        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          Your Public Contributions
        </ParagraphHeader>
        <p
          className={`px-[16px] pb-[40px] text-[18px] font-[400] leading-[137%]`}
        >
          When you make a contribution to the Site, by creating or editing a
          profile, you are creating a permanent, public record of every piece of
          content added, removed, or altered by you. The page history will show
          when your contribution or deletion was made, as well as your username
          (if you are signed in) or your IP address (if you are not signed in).
          We may use your public contributions, either aggregated with the
          public contributions of others or individually, to create new features
          or data-related products for you or to learn more about how the Site
          is used, as further explained below in the ”How We Use Information We
          Receive From You” section of this Privacy Policy.
          <br />
          <br />
          Unless this Policy says otherwise, you should assume that information
          that you actively contribute to the Site, including Personal
          Information, is publicly visible and can be found by search engines.
          Like most things on the Internet, anything you share may be copied and
          redistributed throughout the Internet by other people. Please do not
          contribute any information that you are uncomfortable making
          permanently public.
        </p>

        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          Account Information & Registration
        </ParagraphHeader>
        <p
          className={`px-[16px] pb-[40px] text-[18px] font-[400] leading-[137%]`}
        >
          Want to create an account? Great! Don't want to create an account? No
          problem! <br /> <br /> You are not required to create an account to
          read or contribute to the Site, except under rare circumstances (see
          below). However, if you contribute without signing in, your
          contribution will be publicly attributed to the IP address associated
          with your device. <br /> <br /> If you want to create a standard
          account, in most cases we require only a username and a password. Just
          be aware that if you choose not to provide an email address, we won’t
          be able to help you recover your password. Another restriction is that
          you won’t be allowed to edit the date or place of death in any human’s
          record. <br /> <br /> Your username will be publicly visible, so
          please be careful about revealing your real name or other Personal
          Information in your username. Your password is only used to verify
          that the account is yours. Your IP address is also automatically
          submitted to us, and we record it temporarily. This is to protect the
          Population Project users’ content; in the event of abuse, IP addresses
          may be associated with usernames as part of an investigation. No other
          Personal Information is required: no name, no email address, and no
          credit card information. <br /> <br /> To gain a better understanding
          of the demographics of our users, to localize our services and to
          learn how we can improve our services, we may ask you for more
          demographic information, such as gender or age, about yourself. We
          will tell you if such information is intended to be public or private,
          so that you can make an informed decision about whether you want to
          provide us with that information. Providing such information is always
          completely optional. If you don't want to, you don't have to—it's as
          simple as that.
        </p>

        <ParagraphHeader
          className="mb-[25px] mt-[30px] px-[16px]"
          fontWeight={700}
        >
          Location Information
        </ParagraphHeader>
        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          GPS & Other Location Technologies
        </ParagraphHeader>
        <p
          className={`px-[16px] pb-[25px] text-[18px] font-[400] leading-[137%]`}
        >
          If you consent, we can use GPS (and other technologies commonly used
          to determine location) to show you more relevant content. We keep
          information obtained by these technologies confidential, except as
          provided in this Policy.
        </p>
        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          IP Addresses
        </ParagraphHeader>
        <p
          className={`px-[16px] pb-[40px] text-[18px] font-[400] leading-[137%]`}
        >
          When you visit the Site, we automatically receive the IP address of
          the device (or your proxy server) you are using to access the
          Internet, which could be used to infer your geographical location.
        </p>

        <ParagraphHeader
          className="information mb-[25px] mt-[30px] px-[16px]"
          fontWeight={700}
        >
          Information Related to Your Use of the Population Project Site
        </ParagraphHeader>
        <p
          className={`px-[16px] pb-[40px] text-[18px] font-[400] leading-[137%]`}
        >
          We want to make the Population Project Site better for you by learning
          more about how you use it. Examples of this might include how often
          you visit the Site, what you like, how you get to the Site, and
          whether you would use a helpful feature more if we explained it
          differently.
        </p>

        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          Information We Receive Automatically
        </ParagraphHeader>
        <p
          className={`px-[16px] pb-[40px] text-[18px] font-[400] leading-[137%]`}
        >
          Because of how browsers work, we receive some information
          automatically when you visit the Population Project Site. This
          information includes the type of device you are using (possibly
          including unique device identification numbers), the type and version
          of your browser, your browser's language preference, the type and
          version of your device's operating system, in some cases the name of
          your internet service provider or mobile carrier, the website that
          referred you to our Site, which pages you request and visit, and the
          date and time of each request you make to the Population Project Site.{' '}
          <br /> <br /> Put simply, we use this information to enhance your
          experience with our Site. For example, we use this information to
          administer the Site, provide greater security, and fight vandalism;
          optimize mobile experience, customize content and set language
          preferences, test features to see what works, and improve performance;
          understand how users interact with the Site, track and study use of
          various features, gain understanding about the demographics of the
          Site, and analyze trends.
        </p>
        <ParagraphHeader
          className="information mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          Information We Collect
        </ParagraphHeader>
        <p
          className={`px-[16px] pb-[40px] text-[18px] font-[400] leading-[137%]`}
        >
          We actively collect some types of information with a variety of
          commonly-used technologies. These generally include tracking pixels,
          JavaScript, and a variety of "locally stored data" technologies, such
          as cookies and local storage. These types of technologies may also be
          used in online tools on a third-party site that loads information from
          the Population Project Site. We realize that some of these
          technologies do not have the best reputation and can be used for
          less-than-noble purposes. So we want to be as clear as we can about
          why we use these methods and the type of information we collect with
          them. <br /> <br />
          Depending on which technology we use, locally stored data may include
          text, Personal Information (like your IP address), and information
          about your use of the Population Project Site (like your username or
          the time of your visit). <br /> <br /> We use this information to make
          your experience with the Population Project Site safer and better, to
          gain a greater understanding of user preferences and their interaction
          with the Site, and to generally improve our services. We will never
          use third-party cookies, unless we get your permission to do so. If
          you ever come across a third-party data collection tool that has not
          been authorized by you (such as one that may have been mistakenly
          placed by another user or administrator), please report it to us at{' '}
          <a
            className="cursor-pointer text-blue-600 visited:text-purple-600 hover:text-blue-800"
            href="mailto:privacy@thepopulationproject.org"
            target="_blank"
            rel="noreferrer"
          >
            privacy@thepopulationproject.org
          </a>
          . <br /> <br /> We believe this data collection helps improve your
          user experience, but you may remove or disable some or all locally
          stored data through your browser settings, depending on your browser.
          While locally stored data may not be necessary to use our sites, some
          features will not function properly if you disable locally stored
          data.
          <br /> <br />
          Please note that some information about the actions taken by your
          username is made publicly available through public logs alongside
          actions taken by other users. For example, a public log may
        </p>

        <ParagraphHeader
          className="how-we mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          How We Use Information We Receive From You
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[40px] text-[18px] font-[400] leading-[137%]`}
        >
          We and our service providers use your information for the legitimate
          purpose of pursuing our charitable mission, including:
        </p>
        <div className="flex px-[16px]">
          <p className="mr-[10px]">&#183;</p>
          <p className="text-[18px] font-[400] leading-[137%]">
            Operating the Site, sharing your contributions and administering our
            Services.
          </p>
        </div>
        <div className="flex px-[16px]">
          <p className="mr-[10px]">&#183;</p>
          <p className="text-[18px] font-[400] leading-[137%]">
            Providing customized services.
          </p>
        </div>
        <div className="flex px-[16px]">
          <p className="mr-[10px]">&#183;</p>
          <p className="text-[18px] font-[400] leading-[137%]">
            Sending emails with news updates and communications about items we
            believe may be of interest to you.
          </p>
        </div>
        <div className="flex px-[16px]">
          <p className="mr-[10px]">&#183;</p>
          <p className="text-[18px] font-[400] leading-[137%]">
            Improving the Population Project Site and making your user
            experience safer and better.
          </p>
        </div>

        <ParagraphHeader
          className="other mb-[25px] mt-[30px] px-[16px]"
          fontWeight={700}
        >
          Other
        </ParagraphHeader>
        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          Location Information
        </ParagraphHeader>
        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          GPS & Other Location Technologies
        </ParagraphHeader>
        <p
          className={`px-[16px] pb-[40px] text-[18px] font-[400] leading-[137%]`}
        >
          We and our service providers use your information for the legitimate
          purpose of pursuing our charitable mission, including:
        </p>
        <div className="flex px-[16px]">
          <p className="mr-[10px]">&#183;</p>
          <p className="text-[18px] font-[400] leading-[137%]">
            Operating the Site, sharing your contributions and administering our
            Services.
          </p>
        </div>
        <div className="flex px-[16px]">
          <p className="mr-[10px]">&#183;</p>
          <p className="text-[18px] font-[400] leading-[137%]">
            Providing customized services.
          </p>
        </div>
        <div className="flex px-[16px]">
          <p className="mr-[10px]">&#183;</p>
          <p className="text-[18px] font-[400] leading-[137%]">
            Sending emails with news updates and communications about items we
            believe may be of interest to you.
          </p>
        </div>
        <div className="mb-[40px] flex px-[16px]">
          <p className="mr-[10px]">&#183;</p>
          <p className="text-[18px] font-[400] leading-[137%]">
            Improving the Population Project Site and making your user
            experience safer and better.
          </p>
        </div>

        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          IP Addresses
        </ParagraphHeader>
        <p
          className={`px-[16px] pb-[40px] text-[18px] font-[400] leading-[137%]`}
        >
          When you visit the Population Project Site, we automatically receive
          the IP address of the device (or your proxy server) you are using to
          access the Internet, which could be used to infer your geographical
          location. We keep IP addresses confidential, except as provided in
          this Policy. <br /> <br /> We use this location information to make
          your experience with the Population Project Site safer and better, to
          gain a greater understanding of user preferences and their interaction
          with the Site, and to generally improve our services. For example, we
          use this information to provide greater security, optimize mobile
          experience, and learn how to expand and better support our community
          of users. We also use Personal Information in the manner described in
          the sections of this Policy titled "For Legal Reasons" and "To Protect
          You, Ourselves & Others."
        </p>

        <H3 fontSize={20} className="mt-[72px] px-[16px]">
          Sharing
        </H3>
        <div className="mx-[16px] mb-[30px] mt-[6px] h-px bg-[#D1D1D1]" />

        <ParagraphHeader
          className="when-may mb-[25px] px-[16px]"
          fontSize={18}
          fontWeight={700}
        >
          When May We Share Your Information?
        </ParagraphHeader>

        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          With Your Permission
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          This has never happened but should we wish to share your Information,
          we would obtain your agreement first.
        </p>

        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          For Legal Reasons
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          We will access, use, preserve, and/or disclose your Personal
          Information if we reasonably believe it necessary to satisfy a valid
          and legally enforceable warrant, subpoena, court order, law or
          regulation, or other judicial or administrative order. However, if we
          believe that a particular request for disclosure of a user's
          information is legally invalid or an abuse of the legal system and the
          affected user does not intend to oppose the disclosure themselves, we
          will try our best to fight it. We are committed to notifying you via
          email at least ten (10) calendar days, when possible, before we
          disclose your Personal Information in response to a legal demand.
          However, we may only provide notice if we are not legally restrained
          from contacting you, there is no credible threat to life or limb that
          is created or increased by disclosing the request, and you have
          provided us with an email address. <br /> <br /> Nothing in this
          Privacy Policy is intended to limit any legal objections or defenses
          you may have to a third-party's request (whether it be civil,
          criminal, or governmental) to disclose your Personal Information. We
          recommend seeking the advice of legal counsel immediately if such a
          request is made involving you.
        </p>

        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          If the Organization is Transferred (Really Unlikely!)
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          In the extremely unlikely event that ownership of all or substantially
          all of the Population Project changes, or we go through a
          reorganization (such as a merger, consolidation, or acquisition),
          consistent with our legitimate interest, we will continue to keep your
          Personal Information confidential, except as provided in this Policy,
          and provide notice to you via the Population Project Site and a
          notification through email at least thirty (30) calendar days before
          any Personal Information is transferred or becomes subject to a
          different privacy policy.
        </p>

        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          To Protect You, Ourselves, & Others
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          We hope that this never comes up, but we may disclose your Personal
          Information if we believe that it's reasonably necessary to prevent
          imminent and serious bodily harm or death to a person, or to protect
          our organization, employees, contractors, users, or the public. We may
          also disclose your Personal Information if we reasonably believe it
          necessary to detect, prevent, or otherwise assess and address
          potential spam, malware, fraud, abuse, unlawful activity, and security
          or technical concerns.
        </p>

        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          To Our Service Providers
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          We use third-party service providers or contractors to help run or
          improve the Population Project Site for you and other users. We give
          access to your Personal Information to these providers or contractors
          as needed to perform their services for us or to use their tools and
          services. We put requirements, such as confidentiality agreements, in
          place to help ensure that these service providers treat your Personal
          Information consistently with, and no less protective of your privacy
          than, the principles of this Policy.
        </p>

        <ParagraphHeader
          className="mb-[12px] px-[16px]"
          fontWeight={600}
          fontSize={18}
        >
          Because You Made It Public
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[56px] text-[18px] font-[400] leading-[137%]`}
        >
          Any information you post publicly on the Population Project is just
          that – public. And if you edit without registering or logging into
          your account, your IP address will be seen publicly.
        </p>

        <H3 fontSize={20} className="mt-[72px] px-[16px]">
          Protection
        </H3>
        <div className="mx-[16px] mb-[30px] mt-[6px] h-px bg-[#D1D1D1]" />

        <ParagraphHeader
          className="how-do mb-[25px] px-[16px]"
          fontWeight={700}
        >
          How Do We Protect Your Personal Information?
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          We strive to protect your Personal Information from unauthorized
          access, use, or disclosure. We use a variety of physical and technical
          measures, policies, and procedures (such as access control procedures,
          network firewalls, and physical security) designed to protect our
          systems and your Personal Information. Unfortunately, there's no such
          thing as completely secure data transmission or storage, so we can't
          guarantee that our security will not be breached (by technical
          measures or through violation of our policies and procedures). <br />{' '}
          <br /> We will never ask for your password by email (but may send you
          a temporary password via email if you have requested a password
          reset). If you ever receive an email that requests your password,
          please let us know by sending it to{' '}
          <a
            className="cursor-pointer text-blue-600 visited:text-purple-600 hover:text-blue-800"
            href="mailto:privacy@thepopulationproject.org"
            target="_blank"
            rel="noreferrer"
          >
            privacy@thepopulationproject.org
          </a>
          , so we can investigate the source of the email.
        </p>

        <ParagraphHeader
          className="how-long mb-[25px] mt-[30px] px-[16px]"
          fontWeight={700}
        >
          How Long Do We Keep Your Data?
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          Once we receive Personal Information from you, we keep it for the
          shortest possible time that is consistent with the maintenance,
          understanding, and improvement of the Population Project Site, and our
          obligations under applicable law. In most instances, Personal
          Information is deleted, aggregated or de-identified after 90 days.
          Non-Personal Information may be retained indefinitely as appropriate.{' '}
          <br /> <br /> Please remember that when you make a contribution to the
          Population Project Site, the page history will show when your
          contribution was made, your username (if you are signed in), or your
          IP address (if you edit while not logged in). The transparency of the
          projects' contribution and revision histories is critical to their
          efficacy and trustworthiness.
        </p>

        <ParagraphHeader
          className="your-rights mb-[25px] mt-[30px] px-[16px]"
          fontWeight={700}
        >
          Your Rights
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[200px] text-[18px] font-[400] leading-[137%]`}
        >
          If you would like to request to access, update or restrict/object to
          the processing of Personal Information, or receive a copy of your
          Personal Information for purposes of transmitting it to another
          organization, you may contact us at{' '}
          <a
            className="cursor-pointer text-blue-600 visited:text-purple-600 hover:text-blue-800"
            href="mailto:privacy@thepopulationproject.org"
            target="_blank"
            rel="noreferrer"
          >
            privacy@thepopulationproject.org
          </a>
          .
        </p>

        <H3 fontSize={20} className="mt-[72px] px-[16px]">
          Important Info
        </H3>
        <div className="mx-[16px] mb-[30px] mt-[6px] h-px bg-[#D1D1D1]" />

        <p className="px-[16px] text-[18px] font-[400] italic leading-[137%]">
          For the protection of the Population Project and other users, if you
          do not agree with this Privacy Policy, you may not use the Population
          Project website.
        </p>

        <ParagraphHeader
          className="where mb-[25px] mt-[30px] px-[16px]"
          fontWeight={700}
        >
          Where is the Population Project & What Does That Mean for Me?
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          The Population Project is a non-profit organization incorporated in
          the State of New York, with servers located in the U.S. If you decide
          to use the Population Project Site, whether from inside or outside of
          the U.S., you understand that your Personal Information will be
          collected, transferred, stored, processed, disclosed and otherwise
          used in the U.S. as described in this Privacy Policy. You also
          understand that your information may be transferred by us from the
          U.S. to other countries, which may have different or less stringent
          data protection laws than your country, in connection with providing
          services to you.
        </p>

        <ParagraphHeader
          className="our mb-[25px] mt-[30px] px-[16px]"
          fontWeight={700}
        >
          Our Response to Do Not Track (DNT) Signals
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          We don’t share your Personal Information for marketing purposes.
          Because we protect all users in accordance with this Privacy Policy,
          we do not change our behavior in response to a web browser's "do not
          track" signal.
        </p>

        <ParagraphHeader
          className="changes mb-[25px] mt-[30px] px-[16px]"
          fontWeight={700}
        >
          Changes to This Privacy Policy
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          This Privacy Policy may be updated. The most recent version can be
          found at{' '}
          <Link
            className="cursor-pointer text-blue-600 visited:text-purple-600 hover:text-blue-800"
            href="https://thepopulationproject.org/privacy"
          >
            https://thepopulationproject.org/privacy
          </Link>
          .
        </p>

        <ParagraphHeader
          className="contact mb-[25px] mt-[30px] px-[16px]"
          fontWeight={700}
        >
          Contact Us
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[26px] text-[18px] font-[400] leading-[137%]`}
        >
          If you have questions or suggestions about this Privacy Policy, or the
          information collected under this Privacy Policy, please email us at{' '}
          <a
            className="cursor-pointer text-blue-600 visited:text-purple-600 hover:text-blue-800"
            href="mailto:privacy@thepopulationproject.org"
            target="_blank"
            rel="noreferrer"
          >
            privacy@thepopulationproject.org
          </a>
          .
        </p>

        <ParagraphHeader
          className="thank-you mb-[25px] mt-[30px] px-[16px]"
          fontWeight={700}
        >
          Thank You!
        </ParagraphHeader>

        <p
          className={`px-[16px] pb-[200px] text-[18px] font-[400] leading-[137%]`}
        >
          Thank you for reading our Privacy Policy. We hope you enjoy using the
          Population Project Site and appreciate your participation in creating,
          maintaining, and constantly improving the first worldwide register of
          living humans.
        </p>
      </div>
      <div className={`hidden justify-between xl:flex`}>
        <div className={`flex w-full flex-col md:flex-[1]`}>
          <p
            className={`mb-[14px] mt-[72px] pl-[50px] text-[18px] font-[600] leading-[137%] opacity-70`}
          >
            Table of Contents
          </p>
          <div className="flex flex-col">
            <button
              onClick={handleActiveItem('summary')}
              className={`flex h-[42px] items-center pl-[50px] ${
                activeItem === 'summary'
                  ? 'bg-[#409F8510]'
                  : 'hover:bg-[#ECECEC]'
              }`}
            >
              <SecondaryParagraph
                className={`text-left transition ${
                  activeItem === 'summary'
                    ? 'font-[600] text-[#409F85]'
                    : 'font-[500]'
                }`}
                fontWheight={activeItem === 'summary' ? 600 : 400}
              >
                Summary
              </SecondaryParagraph>
            </button>
            <button
              onClick={handleActiveItem('privacy-policy')}
              className={`flex h-[42px] items-center pl-[50px] ${
                activeItem === 'privacy-policy'
                  ? 'bg-[#409F8510]'
                  : 'hover:bg-[#ECECEC]'
              }`}
            >
              <SecondaryParagraph
                className={`text-left transition ${
                  activeItem === 'privacy-policy'
                    ? 'font-[600] text-[#409F85]'
                    : 'font-[500]'
                }`}
                fontWheight={activeItem === 'privacy-policy' ? 600 : 400}
              >
                Privacy Policy
              </SecondaryParagraph>
            </button>
            <div className="flex flex-col">
              <button
                onClick={handleActiveItem('introduction')}
                className={`flex h-[42px] items-center pl-[80px] ${
                  activeItem === 'introduction'
                    ? 'bg-[#409F8510]'
                    : 'hover:bg-[#ECECEC]'
                }`}
              >
                <SecondaryParagraph
                  className={`text-left transition ${
                    activeItem === 'introduction'
                      ? 'font-[600] text-[#409F85]'
                      : 'font-[500]'
                  }`}
                  fontWheight={activeItem === 'introduction' ? 600 : 400}
                >
                  Introduction
                </SecondaryParagraph>
              </button>
              <button
                onClick={handleActiveItem('collection')}
                className={`flex h-[42px] items-center pl-[80px] ${
                  activeItem === 'collection'
                    ? 'bg-[#409F8510]'
                    : 'hover:bg-[#ECECEC]'
                }`}
              >
                <SecondaryParagraph
                  className={`text-left transition ${
                    activeItem === 'collection'
                      ? 'font-[600] text-[#409F85]'
                      : 'font-[500]'
                  }`}
                  fontWheight={activeItem === 'collection' ? 600 : 400}
                >
                  Collection & Use of Info
                </SecondaryParagraph>
              </button>
              <button
                onClick={handleActiveItem('sharing')}
                className={`flex h-[42px] items-center pl-[80px] ${
                  activeItem === 'sharing'
                    ? 'bg-[#409F8510]'
                    : 'hover:bg-[#ECECEC]'
                }`}
              >
                <SecondaryParagraph
                  className={`text-left transition ${
                    activeItem === 'sharing'
                      ? 'font-[600] text-[#409F85]'
                      : 'font-[500]'
                  }`}
                  fontWheight={activeItem === 'sharing' ? 600 : 400}
                >
                  Sharing
                </SecondaryParagraph>
              </button>
              <button
                onClick={handleActiveItem('protection')}
                className={`flex h-[42px] items-center pl-[80px] ${
                  activeItem === 'protection'
                    ? 'bg-[#409F8510]'
                    : 'hover:bg-[#ECECEC]'
                }`}
              >
                <SecondaryParagraph
                  className={`text-left transition ${
                    activeItem === 'protection'
                      ? 'font-[600] text-[#409F85]'
                      : 'font-[500]'
                  }`}
                  fontWheight={activeItem === 'protection' ? 600 : 400}
                >
                  Protection
                </SecondaryParagraph>
              </button>
              <button
                onClick={handleActiveItem('important')}
                className={`flex h-[42px] items-center pl-[80px] ${
                  activeItem === 'important'
                    ? 'bg-[#409F8510]'
                    : 'hover:bg-[#ECECEC]'
                }`}
              >
                <SecondaryParagraph
                  className={`text-left transition ${
                    activeItem === 'important'
                      ? 'font-[600] text-[#409F85]'
                      : 'font-[500]'
                  }`}
                  fontWheight={activeItem === 'important' ? 600 : 400}
                >
                  Important Info
                </SecondaryParagraph>
              </button>
            </div>
          </div>
        </div>
        <div
          className={`w-full border-x-DEFAULT border-x-[#DCDCDC] md:flex-[3]`}
        >
          {activeItem === 'summary' && (
            <div className="flex flex-col px-[63px]">
              <H3 className="mt-[72px]">Summary of the Privacy Policy</H3>
              <div className="mb-[30px] mt-[6px] h-px w-full bg-[#D1D1D1]" />
              <p className={`pb-[26px] text-[20px] font-[400] leading-[137%]`}>
                This is a summary of the Privacy Policy, To read the full terms,
                choose a page in the table of contents to the left.
              </p>

              <p
                className={`pb-[48px] text-[18px] font-[400] italic leading-[137%]`}
              >
                Disclaimer: This summary is not a part of the Privacy Policy and
                is not a legal document. It is simply a handy reference for
                understanding the full Privacy Policy. Think of it as the
                user-friendly interface to our Privacy Policy.
              </p>
              <div
                className={`mx-auto mb-[30px] bg-[#409F8510]  px-[25px] py-[20px]`}
              >
                <p className={`text-[18px] font-[600] leading-[137%] `}>
                  Because we believe that you shouldn’t have to provide personal
                  information to participate to the Population Project, you may:
                </p>
                <div className="ml-[60px] flex w-auto flex-col">
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Read, edit, or use the Population Project Site without
                      registering for an account.
                    </p>
                  </div>
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Register for an account without providing an email address
                      or real name.
                    </p>
                  </div>
                </div>

                <p
                  className={`pt-[30px] text-[18px] font-[600] leading-[137%] `}
                >
                  Because we want to understand how the Population Project Site
                  is used so we can make it better for you, we collect some
                  information from you:
                </p>
                <div className="ml-[60px] flex w-auto flex-col">
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Make public contributions.
                    </p>
                  </div>
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Register an account or update your user page.
                    </p>
                  </div>
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Use the Population Project Site.
                    </p>
                  </div>
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Send us emails or participate in a survey or give
                      feedback.
                    </p>
                  </div>
                </div>

                <p
                  className={`pt-[30px] text-[18px] font-[600] leading-[137%] `}
                >
                  We are committed to:
                </p>
                <div className="ml-[60px] flex w-auto flex-col">
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Describing how your information may be used or shared in
                      this Privacy Policy.
                    </p>
                  </div>
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Using reasonable measures to keep your information secure.
                    </p>
                  </div>
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Never selling your information or sharing it with third
                      parties for marketing purposes.
                    </p>
                  </div>
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Only sharing your information in limited circumstances,
                      such as to improve the Population Project Site or to
                      comply with the law.
                    </p>
                  </div>
                </div>

                <p
                  className={`pt-[30px] text-[18px] font-[600] leading-[137%] `}
                >
                  Be aware:
                </p>
                <div className="ml-[60px] flex w-auto flex-col">
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      Any content you add or any changes that you make to the
                      Population Project Site will be publicly and permanently
                      available.
                    </p>
                  </div>
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      If you add content or make a change to the Population
                      Project Site without logging in, that content or change
                      will be publicly and permanently attributed to the IP
                      address used at the time rather than a username.
                    </p>
                  </div>
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      As part of our commitment to education and research around
                      the world, we may occasionally release public information
                      and aggregated or non-personal information to the general
                      public through data dumps and data sets.
                    </p>
                  </div>
                  <div className="flex pt-[10px]">
                    <p className="mr-[10px]">&#183;</p>
                    <p className="text-[18px] font-[400] leading-[137%]">
                      For the protection of the Population Project and other
                      users, if you do not agree with this Privacy Policy, you
                      may not use the Population Project’s Site.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(activeItem === 'privacy-policy' ||
            activeItem === 'introduction') && (
            <div className="flex flex-col px-[63px]">
              <H3 className="mt-[72px]">Introduction to the Privacy Policy</H3>
              <div className="mb-[30px] mt-[6px] h-px w-full bg-[#D1D1D1]" />
              <p className={`pb-[26px] text-[20px] font-[400] leading-[137%]`}>
                The Population Project relies heavily on the work and
                contributions of volunteers. We believe that
                information-gathering and use should go hand-in-hand with
                transparency. This Privacy Policy explains how the Population
                Project, the non-profit organization that hosts this site,
                collects, uses, and shares information we receive from you
                through your use of the Population Project Site. It is essential
                to understand that, by using the Population Project Site, you
                consent to the collection, transfer, processing, storage,
                disclosure, and use of your information as described in this
                Privacy Policy. That means that reading this Policy carefully is
                important.
                <br />
                <br />
                We believe that you shouldn't have to provide nonpublic Personal
                Information to participate to the Population Project. You do not
                have to provide things like your real name, address, or country
                to sign up for a standard account or contribute content to the
                Population Project Site.
                <br />
                <br />
                We do not sell or rent your Personal Information, nor do we give
                it to others to sell you anything. We use it to figure out how
                to make the Population Project Site more engaging and
                accessible. Put simply: we use this information to make the
                Population Project Site better for you.
                <br />
                <br />
                Because everyone (not just lawyers) should be able to easily
                understand how and why their information is collected and used,
                we use common language instead of more formal terms throughout
                this Policy. To help ensure your understanding of some
                particular key terms, here is a table of translations:
              </p>

              <div className={`mx-auto mb-[30px] flex flex-col`}>
                <div className="flex">
                  <div className="flex w-[207px] items-center justify-center border-r-[1.5px] border-r-[#E8E8E8] bg-[#409F8515]">
                    <p className="py-[14px] text-[16px] font-[600] leading-[137%]">
                      When we say...
                    </p>
                  </div>
                  <div className="flex w-[498px] items-center justify-center bg-[#409F8515]">
                    <p className="py-[14px] text-[16px] font-[600] leading-[137%]">
                      ...we mean:
                    </p>
                  </div>
                </div>
                <TableSection
                  leftText="“the Population Project” / ”we” / ”us” / ”our”"
                  rightText="The Population Project, the non-profit organization that
                  operates this Site."
                />
                <TableSection
                  leftText="“the Population Project Site” / ”the Site” / ”our services”"
                  rightText="The site whose URL is thepopulationproject.org, as well as mobile applications, Application Programming Interfaces (APIs), emails, and notifications."
                />
                <TableSection
                  leftText="“you” / “your” / “me”"
                  rightText="You, regardless of whether you are an individual, group, or organization, and regardless of whether you are using the Site on behalf of yourself or someone else."
                />
                <TableSection
                  leftText="“this Policy” / “this Privacy Policy”"
                  rightText="This document, entitled the “Population Project Privacy Policy”."
                />
                <TableSection
                  leftText="“contributions”"
                  rightText="Content you add or changes you make to the Site."
                />
                <div className="flex">
                  <div className="flex w-[207px] items-center justify-center border-r-[1.5px] border-t-[1.5px] border-r-[#E8E8E8] border-t-[#E8E8E8] bg-[#409F8505]">
                    <p className="p-[10px] text-[14px] font-[400] leading-[137%]">
                      “Personal Information”
                    </p>
                  </div>
                  <div className="flex w-[498px] flex-col items-center justify-center border-t-[1.5px] border-t-[#E8E8E8] bg-[#409F8505]">
                    <p className="p-[10px] text-[14px] font-[400] leading-[137%]">
                      Information you provide us or information we collect that
                      could be used to personally identify you. To be clear,
                      while we do not necessarily collect the following types of
                      information, we consider at least the following to be
                      “Personal Information” if it is otherwise nonpublic and
                      can be used to identify you:
                    </p>
                    <p className="ml-[28px] p-[10px] text-[14px] font-[400] leading-[137%]">
                      (a) your real name, address, phone number, email address,
                      password, identification number on government-issued ID,
                      IP address, user-agent information, payment and account
                      number (b) when associated with one of the items in
                      subsection (a), any sensitive data such as date of birth,
                      gender, sexual orientation, racial or ethnic origins,
                      marital or familial status, medical conditions or
                      disabilities, political affiliation, and religion.
                    </p>
                  </div>
                </div>
                <TableSection
                  leftText="“third party” / “third parties”"
                  rightText="Individuals, entities, websites, services, products, and applications that are not controlled, managed, or operated by the Population Project."
                />
              </div>
            </div>
          )}
          {activeItem === 'collection' && (
            <div className="flex flex-col px-[63px]">
              <H3 className="mt-[72px]">Collection & Use of Info</H3>
              <div className="mb-[30px] mt-[6px] h-px w-full bg-[#D1D1D1]" />
              <ParagraphHeader className="types mb-[25px]" fontWeight={700}>
                Types of Information We Receive From You & How We Get It
              </ParagraphHeader>
              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                Your Public Contributions
              </ParagraphHeader>
              <p className={`pb-[40px] text-[18px] font-[400] leading-[137%]`}>
                When you make a contribution to the Site, by creating or editing
                a profile, you are creating a permanent, public record of every
                piece of content added, removed, or altered by you. The page
                history will show when your contribution or deletion was made,
                as well as your username (if you are signed in) or your IP
                address (if you are not signed in). We may use your public
                contributions, either aggregated with the public contributions
                of others or individually, to create new features or
                data-related products for you or to learn more about how the
                Site is used, as further explained below in the ”How We Use
                Information We Receive From You” section of this Privacy Policy.
                <br />
                <br />
                Unless this Policy says otherwise, you should assume that
                information that you actively contribute to the Site, including
                Personal Information, is publicly visible and can be found by
                search engines. Like most things on the Internet, anything you
                share may be copied and redistributed throughout the Internet by
                other people. Please do not contribute any information that you
                are uncomfortable making permanently public.
              </p>

              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                Account Information & Registration
              </ParagraphHeader>
              <p className={`pb-[40px] text-[18px] font-[400] leading-[137%]`}>
                Want to create an account? Great! Don't want to create an
                account? No problem! <br /> <br /> You are not required to
                create an account to read or contribute to the Site, except
                under rare circumstances (see below). However, if you contribute
                without signing in, your contribution will be publicly
                attributed to the IP address associated with your device. <br />{' '}
                <br /> If you want to create a standard account, in most cases
                we require only a username and a password. Just be aware that if
                you choose not to provide an email address, we won’t be able to
                help you recover your password. Another restriction is that you
                won’t be allowed to edit the date or place of death in any
                human’s record. <br /> <br /> Your username will be publicly
                visible, so please be careful about revealing your real name or
                other Personal Information in your username. Your password is
                only used to verify that the account is yours. Your IP address
                is also automatically submitted to us, and we record it
                temporarily. This is to protect the Population Project users’
                content; in the event of abuse, IP addresses may be associated
                with usernames as part of an investigation. No other Personal
                Information is required: no name, no email address, and no
                credit card information. <br /> <br /> To gain a better
                understanding of the demographics of our users, to localize our
                services and to learn how we can improve our services, we may
                ask you for more demographic information, such as gender or age,
                about yourself. We will tell you if such information is intended
                to be public or private, so that you can make an informed
                decision about whether you want to provide us with that
                information. Providing such information is always completely
                optional. If you don't want to, you don't have to—it's as simple
                as that.
              </p>

              <ParagraphHeader className="mb-[25px] mt-[30px]" fontWeight={700}>
                Location Information
              </ParagraphHeader>
              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                GPS & Other Location Technologies
              </ParagraphHeader>
              <p className={`pb-[25px] text-[18px] font-[400] leading-[137%]`}>
                If you consent, we can use GPS (and other technologies commonly
                used to determine location) to show you more relevant content.
                We keep information obtained by these technologies confidential,
                except as provided in this Policy.
              </p>
              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                IP Addresses
              </ParagraphHeader>
              <p className={`pb-[40px] text-[18px] font-[400] leading-[137%]`}>
                When you visit the Site, we automatically receive the IP address
                of the device (or your proxy server) you are using to access the
                Internet, which could be used to infer your geographical
                location.
              </p>

              <ParagraphHeader
                className="information mb-[25px] mt-[30px]"
                fontWeight={700}
              >
                Information Related to Your Use of the Population Project Site
              </ParagraphHeader>
              <p className={`pb-[40px] text-[18px] font-[400] leading-[137%]`}>
                We want to make the Population Project Site better for you by
                learning more about how you use it. Examples of this might
                include how often you visit the Site, what you like, how you get
                to the Site, and whether you would use a helpful feature more if
                we explained it differently.
              </p>

              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                Information We Receive Automatically
              </ParagraphHeader>
              <p className={`pb-[40px] text-[18px] font-[400] leading-[137%]`}>
                Because of how browsers work, we receive some information
                automatically when you visit the Population Project Site. This
                information includes the type of device you are using (possibly
                including unique device identification numbers), the type and
                version of your browser, your browser's language preference, the
                type and version of your device's operating system, in some
                cases the name of your internet service provider or mobile
                carrier, the website that referred you to our Site, which pages
                you request and visit, and the date and time of each request you
                make to the Population Project Site. <br /> <br /> Put simply,
                we use this information to enhance your experience with our
                Site. For example, we use this information to administer the
                Site, provide greater security, and fight vandalism; optimize
                mobile experience, customize content and set language
                preferences, test features to see what works, and improve
                performance; understand how users interact with the Site, track
                and study use of various features, gain understanding about the
                demographics of the Site, and analyze trends.
              </p>

              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                Information We Collect
              </ParagraphHeader>
              <p className={`pb-[40px] text-[18px] font-[400] leading-[137%]`}>
                We actively collect some types of information with a variety of
                commonly-used technologies. These generally include tracking
                pixels, JavaScript, and a variety of "locally stored data"
                technologies, such as cookies and local storage. These types of
                technologies may also be used in online tools on a third-party
                site that loads information from the Population Project Site. We
                realize that some of these technologies do not have the best
                reputation and can be used for less-than-noble purposes. So we
                want to be as clear as we can about why we use these methods and
                the type of information we collect with them. <br /> <br />
                Depending on which technology we use, locally stored data may
                include text, Personal Information (like your IP address), and
                information about your use of the Population Project Site (like
                your username or the time of your visit). <br /> <br /> We use
                this information to make your experience with the Population
                Project Site safer and better, to gain a greater understanding
                of user preferences and their interaction with the Site, and to
                generally improve our services. We will never use third-party
                cookies, unless we get your permission to do so. If you ever
                come across a third-party data collection tool that has not been
                authorized by you (such as one that may have been mistakenly
                placed by another user or administrator), please report it to us
                at{' '}
                <a
                  className="cursor-pointer text-blue-600 visited:text-purple-600 hover:text-blue-800"
                  href="mailto:privacy@thepopulationproject.org"
                  target="_blank"
                  rel="noreferrer"
                >
                  privacy@thepopulationproject.org
                </a>
                . <br /> <br /> We believe this data collection helps improve
                your user experience, but you may remove or disable some or all
                locally stored data through your browser settings, depending on
                your browser. While locally stored data may not be necessary to
                use our sites, some features will not function properly if you
                disable locally stored data.
                <br /> <br />
                Please note that some information about the actions taken by
                your username is made publicly available through public logs
                alongside actions taken by other users. For example, a public
                log may
              </p>

              <ParagraphHeader
                className="how mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                How We Use Information We Receive From You
              </ParagraphHeader>

              <p className={`pb-[40px] text-[18px] font-[400] leading-[137%]`}>
                We and our service providers use your information for the
                legitimate purpose of pursuing our charitable mission,
                including:
              </p>
              <div className="flex ">
                <p className="mr-[10px]">&#183;</p>
                <p className="text-[18px] font-[400] leading-[137%]">
                  Operating the Site, sharing your contributions and
                  administering our Services.
                </p>
              </div>
              <div className="flex ">
                <p className="mr-[10px]">&#183;</p>
                <p className="text-[18px] font-[400] leading-[137%]">
                  Providing customized services.
                </p>
              </div>
              <div className="flex ">
                <p className="mr-[10px]">&#183;</p>
                <p className="text-[18px] font-[400] leading-[137%]">
                  Sending emails with news updates and communications about
                  items we believe may be of interest to you.
                </p>
              </div>
              <div className="flex ">
                <p className="mr-[10px]">&#183;</p>
                <p className="text-[18px] font-[400] leading-[137%]">
                  Improving the Population Project Site and making your user
                  experience safer and better.
                </p>
              </div>

              <ParagraphHeader
                className="other mb-[25px] mt-[30px]"
                fontWeight={700}
              >
                Other
              </ParagraphHeader>
              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                Location Information
              </ParagraphHeader>
              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                GPS & Other Location Technologies
              </ParagraphHeader>
              <p className={`pb-[40px] text-[18px] font-[400] leading-[137%]`}>
                We and our service providers use your information for the
                legitimate purpose of pursuing our charitable mission,
                including:
              </p>
              <div className="flex ">
                <p className="mr-[10px]">&#183;</p>
                <p className="text-[18px] font-[400] leading-[137%]">
                  Operating the Site, sharing your contributions and
                  administering our Services.
                </p>
              </div>
              <div className="flex ">
                <p className="mr-[10px]">&#183;</p>
                <p className="text-[18px] font-[400] leading-[137%]">
                  Providing customized services.
                </p>
              </div>
              <div className="flex ">
                <p className="mr-[10px]">&#183;</p>
                <p className="text-[18px] font-[400] leading-[137%]">
                  Sending emails with news updates and communications about
                  items we believe may be of interest to you.
                </p>
              </div>
              <div className="mb-[40px] flex ">
                <p className="mr-[10px]">&#183;</p>
                <p className="text-[18px] font-[400] leading-[137%]">
                  Improving the Population Project Site and making your user
                  experience safer and better.
                </p>
              </div>

              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                IP Addresses
              </ParagraphHeader>
              <p className={`pb-[40px] text-[18px] font-[400] leading-[137%]`}>
                When you visit the Population Project Site, we automatically
                receive the IP address of the device (or your proxy server) you
                are using to access the Internet, which could be used to infer
                your geographical location. We keep IP addresses confidential,
                except as provided in this Policy. <br /> <br /> We use this
                location information to make your experience with the Population
                Project Site safer and better, to gain a greater understanding
                of user preferences and their interaction with the Site, and to
                generally improve our services. For example, we use this
                information to provide greater security, optimize mobile
                experience, and learn how to expand and better support our
                community of users. We also use Personal Information in the
                manner described in the sections of this Policy titled "For
                Legal Reasons" and "To Protect You, Ourselves & Others."
              </p>
            </div>
          )}
          {activeItem === 'sharing' && (
            <div className="flex flex-col px-[63px]">
              <H3 className="mt-[72px]">Sharing</H3>
              <div className="mb-[30px] mt-[6px] h-px w-full bg-[#D1D1D1]" />

              <ParagraphHeader className="mb-[25px]" fontWeight={700}>
                When May We Share Your Information?
              </ParagraphHeader>

              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                With Your Permission
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                This has never happened but should we wish to share your
                Information, we would obtain your agreement first.
              </p>

              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                For Legal Reasons
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                We will access, use, preserve, and/or disclose your Personal
                Information if we reasonably believe it necessary to satisfy a
                valid and legally enforceable warrant, subpoena, court order,
                law or regulation, or other judicial or administrative order.
                However, if we believe that a particular request for disclosure
                of a user's information is legally invalid or an abuse of the
                legal system and the affected user does not intend to oppose the
                disclosure themselves, we will try our best to fight it. We are
                committed to notifying you via email at least ten (10) calendar
                days, when possible, before we disclose your Personal
                Information in response to a legal demand. However, we may only
                provide notice if we are not legally restrained from contacting
                you, there is no credible threat to life or limb that is created
                or increased by disclosing the request, and you have provided us
                with an email address. <br /> <br /> Nothing in this Privacy
                Policy is intended to limit any legal objections or defenses you
                may have to a third-party's request (whether it be civil,
                criminal, or governmental) to disclose your Personal
                Information. We recommend seeking the advice of legal counsel
                immediately if such a request is made involving you.
              </p>

              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                If the Organization is Transferred (Really Unlikely!)
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                In the extremely unlikely event that ownership of all or
                substantially all of the Population Project changes, or we go
                through a reorganization (such as a merger, consolidation, or
                acquisition), consistent with our legitimate interest, we will
                continue to keep your Personal Information confidential, except
                as provided in this Policy, and provide notice to you via the
                Population Project Site and a notification through email at
                least thirty (30) calendar days before any Personal Information
                is transferred or becomes subject to a different privacy policy.
              </p>

              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                To Protect You, Ourselves, & Others
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                We hope that this never comes up, but we may disclose your
                Personal Information if we believe that it's reasonably
                necessary to prevent imminent and serious bodily harm or death
                to a person, or to protect our organization, employees,
                contractors, users, or the public. We may also disclose your
                Personal Information if we reasonably believe it necessary to
                detect, prevent, or otherwise assess and address potential spam,
                malware, fraud, abuse, unlawful activity, and security or
                technical concerns.
              </p>

              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                To Our Service Providers
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                We use third-party service providers or contractors to help run
                or improve the Population Project Site for you and other users.
                We give access to your Personal Information to these providers
                or contractors as needed to perform their services for us or to
                use their tools and services. We put requirements, such as
                confidentiality agreements, in place to help ensure that these
                service providers treat your Personal Information consistently
                with, and no less protective of your privacy than, the
                principles of this Policy.
              </p>

              <ParagraphHeader
                className="mb-[12px]"
                fontWeight={600}
                fontSize={18}
              >
                Because You Made It Public
              </ParagraphHeader>

              <p className={`pb-[56px] text-[18px] font-[400] leading-[137%]`}>
                Any information you post publicly on the Population Project is
                just that – public. And if you edit without registering or
                logging into your account, your IP address will be seen
                publicly.
              </p>
            </div>
          )}
          {activeItem === 'protection' && (
            <div className="flex flex-col px-[63px]">
              <H3 className="mt-[72px]">Protection</H3>
              <div className="mb-[30px] mt-[6px] h-px w-full bg-[#D1D1D1]" />

              <ParagraphHeader className="how-do mb-[25px]" fontWeight={700}>
                How Do We Protect Your Personal Information?
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                We strive to protect your Personal Information from unauthorized
                access, use, or disclosure. We use a variety of physical and
                technical measures, policies, and procedures (such as access
                control procedures, network firewalls, and physical security)
                designed to protect our systems and your Personal Information.
                Unfortunately, there's no such thing as completely secure data
                transmission or storage, so we can't guarantee that our security
                will not be breached (by technical measures or through violation
                of our policies and procedures). <br /> <br /> We will never ask
                for your password by email (but may send you a temporary
                password via email if you have requested a password reset). If
                you ever receive an email that requests your password, please
                let us know by sending it to{' '}
                <a
                  className="cursor-pointer text-blue-600 visited:text-purple-600 hover:text-blue-800"
                  href="mailto:privacy@thepopulationproject.org"
                  target="_blank"
                  rel="noreferrer"
                >
                  privacy@thepopulationproject.org
                </a>
                , so we can investigate the source of the email.
              </p>

              <ParagraphHeader
                className="how-long mb-[25px] mt-[30px]"
                fontWeight={700}
              >
                How Long Do We Keep Your Data?
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                Once we receive Personal Information from you, we keep it for
                the shortest possible time that is consistent with the
                maintenance, understanding, and improvement of the Population
                Project Site, and our obligations under applicable law. In most
                instances, Personal Information is deleted, aggregated or
                de-identified after 90 days. Non-Personal Information may be
                retained indefinitely as appropriate. <br /> <br /> Please
                remember that when you make a contribution to the Population
                Project Site, the page history will show when your contribution
                was made, your username (if you are signed in), or your IP
                address (if you edit while not logged in). The transparency of
                the projects' contribution and revision histories is critical to
                their efficacy and trustworthiness.
              </p>

              <ParagraphHeader
                className="your-rights mb-[25px] mt-[30px]"
                fontWeight={700}
              >
                Your Rights
              </ParagraphHeader>

              <p className={`pb-[200px] text-[18px] font-[400] leading-[137%]`}>
                If you would like to request to access, update or
                restrict/object to the processing of Personal Information, or
                receive a copy of your Personal Information for purposes of
                transmitting it to another organization, you may contact us at{' '}
                <a
                  className="cursor-pointer text-blue-600 visited:text-purple-600 hover:text-blue-800"
                  href="mailto:privacy@thepopulationproject.org"
                  target="_blank"
                  rel="noreferrer"
                >
                  privacy@thepopulationproject.org
                </a>
                .
              </p>
            </div>
          )}
          {activeItem === 'important' && (
            <div className="flex flex-col px-[63px]">
              <H3 className="mt-[72px]">Important Info</H3>
              <div className="mb-[30px] mt-[6px] h-px w-full bg-[#D1D1D1]" />

              <p className="text-[18px] font-[400] italic leading-[137%]">
                For the protection of the Population Project and other users, if
                you do not agree with this Privacy Policy, you may not use the
                Population Project website.
              </p>

              <ParagraphHeader
                className="where mb-[25px] mt-[30px]"
                fontWeight={700}
              >
                Where is the Population Project & What Does That Mean for Me?
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                The Population Project is a non-profit organization incorporated
                in the State of New York, with servers located in the U.S. If
                you decide to use the Population Project Site, whether from
                inside or outside of the U.S., you understand that your Personal
                Information will be collected, transferred, stored, processed,
                disclosed and otherwise used in the U.S. as described in this
                Privacy Policy. You also understand that your information may be
                transferred by us from the U.S. to other countries, which may
                have different or less stringent data protection laws than your
                country, in connection with providing services to you.
              </p>

              <ParagraphHeader
                className="our mb-[25px] mt-[30px]"
                fontWeight={700}
              >
                Our Response to Do Not Track (DNT) Signals
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                We don’t share your Personal Information for marketing purposes.
                Because we protect all users in accordance with this Privacy
                Policy, we do not change our behavior in response to a web
                browser's "do not track" signal.
              </p>

              <ParagraphHeader
                className="changes mb-[25px] mt-[30px]"
                fontWeight={700}
              >
                Changes to This Privacy Policy
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                This Privacy Policy may be updated. The most recent version can
                be found at{' '}
                <Link
                  className="cursor-pointer text-blue-600 visited:text-purple-600 hover:text-blue-800"
                  href="https://thepopulationproject.org/privacy"
                >
                  https://thepopulationproject.org/privacy
                </Link>
                .
              </p>

              <ParagraphHeader
                className="contact mb-[25px] mt-[30px]"
                fontWeight={700}
              >
                Contact Us
              </ParagraphHeader>

              <p className={`pb-[26px] text-[18px] font-[400] leading-[137%]`}>
                If you have questions or suggestions about this Privacy Policy,
                or the information collected under this Privacy Policy, please
                email us at{' '}
                <a
                  className="cursor-pointer text-blue-600 visited:text-purple-600 hover:text-blue-800"
                  href="mailto:privacy@thepopulationproject.org"
                  target="_blank"
                  rel="noreferrer"
                >
                  privacy@thepopulationproject.org
                </a>
                .
              </p>

              <ParagraphHeader
                className="thank-you mb-[25px] mt-[30px]"
                fontWeight={700}
              >
                Thank You!
              </ParagraphHeader>

              <p className={`pb-[200px] text-[18px] font-[400] leading-[137%]`}>
                Thank you for reading our Privacy Policy. We hope you enjoy
                using the Population Project Site and appreciate your
                participation in creating, maintaining, and constantly improving
                the first worldwide register of living humans.
              </p>
            </div>
          )}
        </div>
        <div className={`w-full md:flex-[1]`}>
          <div className={`flex flex-col`}>
            {activeItem === 'summary' && (
              <div className="fixed flex flex-col">
                <p className="mb-[24px] mt-[72px] pl-[39px] text-[18px] font-[600] leading-[137%] opacity-70">
                  On This Page
                </p>
                <button className={`flex h-[44px] items-center pb-[16px]`}>
                  <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  <p
                    className={`ml-[40px] text-[16px] font-[600] leading-[137%] text-[#409F85]`}
                  >
                    Summary
                  </p>
                </button>
              </div>
            )}
            {(activeItem === 'privacy-policy' ||
              activeItem === 'introduction') && (
              <div className="fixed flex flex-col">
                <p className="mb-[24px] mt-[72px] pl-[39px] text-[18px] font-[600] leading-[137%] opacity-70">
                  On This Page
                </p>
                <button className={`flex h-[44px] items-center pb-[16px]`}>
                  <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  <p
                    className={`ml-[40px] text-[16px] font-[600] leading-[137%] text-[#409F85]`}
                  >
                    Introduction
                  </p>
                </button>
              </div>
            )}
            {activeItem === 'collection' && (
              <div className="fixed flex flex-col">
                <p className="mb-[24px] mt-[72px] pl-[39px] text-[18px] font-[600] leading-[137%] opacity-70">
                  On This Page
                </p>
                <button
                  onClick={handleCollectionItem('types')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {collectionItem === 'types' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      collectionItem === 'types'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    Types of Information We Receive
                  </p>
                </button>
                <button
                  onClick={handleCollectionItem('information')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {collectionItem === 'information' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      collectionItem === 'information'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    Information Related to Your Use of the Site
                  </p>
                </button>
                <button
                  onClick={handleCollectionItem('how')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {collectionItem === 'how' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      collectionItem === 'how'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    How We Use Your Information
                  </p>
                </button>
                <button
                  onClick={handleCollectionItem('other')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {collectionItem === 'other' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      collectionItem === 'other'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    Other
                  </p>
                </button>
              </div>
            )}
            {activeItem === 'sharing' && (
              <div className="fixed flex flex-col">
                <p className="mb-[24px] mt-[72px] pl-[39px] text-[18px] font-[600] leading-[137%] opacity-70">
                  On This Page
                </p>
                <button className={`flex h-[44px] items-center pb-[16px]`}>
                  <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  <p
                    className={`ml-[40px] text-left text-[16px] font-[600] leading-[137%] text-[#409F85]`}
                  >
                    When May We Share Your Information?
                  </p>
                </button>
              </div>
            )}
            {activeItem === 'protection' && (
              <div className="fixed flex flex-col">
                <p className="mb-[24px] mt-[72px] pl-[39px] text-[18px] font-[600] leading-[137%] opacity-70">
                  On This Page
                </p>
                <button
                  onClick={handleProtectionItem('how-do')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {protectionItem === 'how-do' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      protectionItem === 'how-do'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    How Do We Protect Your Personal Information?
                  </p>
                </button>
                <button
                  onClick={handleProtectionItem('how-long')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {protectionItem === 'how-long' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      protectionItem === 'how-long'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    How Long Do We Keep Your Data?
                  </p>
                </button>
                <button
                  onClick={handleProtectionItem('your-rights')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {protectionItem === 'your-rights' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      protectionItem === 'your-rights'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    Your Rights
                  </p>
                </button>
              </div>
            )}
            {activeItem === 'important' && (
              <div className="fixed flex flex-col">
                <p className="mb-[24px] mt-[72px] pl-[39px] text-[18px] font-[600] leading-[137%] opacity-70">
                  On This Page
                </p>
                <button
                  onClick={handleImportantItem('where')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {importantItem === 'where' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      importantItem === 'where'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    Where is the Population Project?
                  </p>
                </button>
                <button
                  onClick={handleImportantItem('our')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {importantItem === 'our' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      importantItem === 'our'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    Our Response to DNT Signals
                  </p>
                </button>
                <button
                  onClick={handleImportantItem('changes')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {importantItem === 'changes' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      importantItem === 'changes'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    Changes to This Privacy Policy
                  </p>
                </button>
                <button
                  onClick={handleImportantItem('contact')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {importantItem === 'contact' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      importantItem === 'contact'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    Contact Us
                  </p>
                </button>
                <button
                  onClick={handleImportantItem('thank-you')}
                  className={`my-[2px] flex h-[44px] items-center pb-[16px]`}
                >
                  {importantItem === 'thank-you' && (
                    <div className={`h-[44px] w-[3px] bg-[#409F85]`} />
                  )}
                  <p
                    className={`text-left transition ${
                      importantItem === 'thank-you'
                        ? 'ml-[40px] font-[600] text-[#409F85]'
                        : 'ml-[48px] font-[400]'
                    } text-[16px] leading-[137%]`}
                  >
                    Thank You!
                  </p>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <NavFooter className="mt-auto" />
    </div>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const countries = await searchForAllCountriesStaticProps();

  return {
    props: {
      ...(await serverSideTranslations(locale || 'en', [
        'common',
        'about',
        'partners',
        'navbar',
        'new-homepage',
        'new-navbar',
        'new-footer',
        'privacy',
      ])),
      countries: countries || [],
    },
  };
};
